import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import {
  conversationEvaluationGetEvaluationProtocols,
  conversationEvaluationCreateEvaluationProtocol,
  conversationEvaluationUpdateEvaluationProtocol,
  conversationEvaluationDeleteEvaluationProtocol,
  conversationEvaluationCreateEvaluationProtocolCategory,
  conversationEvaluationUpdateEvaluationProtocolCategory,
  conversationEvaluationDeleteEvaluationProtocolCategory,
  conversationEvaluationReorderEvaluationProtocolCategories,
  conversationEvaluationShareEvaluationProtocol,
  conversationEvaluationUnshareEvaluationProtocol,
  conversationEvaluationUpdateProtocolCompanyAccess,
  conversationEvaluationCreateEvaluationProtocolElement,
  conversationEvaluationUpdateEvaluationProtocolElement,
  conversationEvaluationDeleteEvaluationProtocolElement,
  conversationEvaluationReorderEvaluationProtocolElements,
  conversationEvaluationGetProtocolUsage,
} from '@/api-gen/api';
import {
  type EvaluationProtocolDto as ApiEvaluationProtocolDto,
  type EvaluationProtocolCategoryDto as ApiEvaluationProtocolCategoryDto,
  // Keep API element DTO type for responses (not used by UI directly)
  type EvaluationProtocolElementDto as ApiEvaluationProtocolElementDto,
  type CreateEvaluationProtocolElementCommand,
  type UpdateEvaluationProtocolElementCommand,
  type CreateEvaluationProtocolCategoryCommand,
  type UpdateEvaluationProtocolCategoryCommand,
  type UpdateEvaluationProtocolCommand,
  type CreateEvaluationProtocolCommand,
  type ShareEvaluationProtocolCommand,
  type SimpleCompanyDto,
  type UpdateEvaluationProtocolCompanyAccessCommand,
  type UnshareEvaluationProtocolCommand,
} from '@/api-gen/api.schemas';
import type { EvaluationProtocolElementType } from '@/api-gen/api.schemas';
import { AccessLevel } from '@/api-gen/api.schemas';

const EVALUATION_PROTOCOL_QUERY_KEY = 'evaluationProtocols';

// Helper type for an element, explicitly defined to avoid circular dependencies from ApiEvaluationProtocolElementDto
export interface TransformedElement {
  // Fields from ApiEvaluationProtocolElementDto that are needed
  id: number;
  description: string;
  order?: number;
  type?: EvaluationProtocolElementType;
  choices?: string | null;
  scaleMin?: number | null;
  scaleMax?: number | null;
  explanationEnabled?: boolean;
  // Added property
  categoryId: number; // Renamed
  // Note: If ApiEvaluationProtocolElementDto has other SCALAR properties needed by the UI,
  // they must be explicitly added here and mapped in the transformation.
}

// Helper type for a category, explicitly defined
export interface TransformedCategory { // Renamed
  // Fields from ApiEvaluationProtocolCategoryDto that are needed
  id: number;
  title: string; // Mapped from 'name'
  protocolId: number; // Mapped from 'evaluationProtocolId'
  order: number;
  // description?: string | null; // Example: if ApiEvaluationProtocolCategoryDto.description is needed
  // Other scalar fields from ApiEvaluationProtocolCategoryDto can be added here if necessary
  elements: TransformedElement[];
}

// Define a company with access level information
export interface CompanyWithAccess extends SimpleCompanyDto {
  access: AccessLevel;
}

// Helper type for a protocol, explicitly defined
export interface TransformedProtocol {
  // Fields from ApiEvaluationProtocolDto that are needed
  id: number;
  title: string; // Mapped from 'name'
  languageCode: string;
  // description?: string | null; // Example: if ApiEvaluationProtocolDto.description is needed
  // Other scalar fields from ApiEvaluationProtocolDto can be added here if necessary
  categories: TransformedCategory[]; // Renamed
  otherCompaniesCount?: number;

  /**
   * Now holds each company's ID and its access
   */
  companies?: Array<{
    companyId: number;
    access?: AccessLevel;
  }>;
}

export const useGetEvaluationProtocolsWithCategories = (options?: { enabled?: boolean }) => { // Renamed
  return useQuery<TransformedProtocol[], Error>({
    queryKey: [EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'], // Renamed
    staleTime: 10 * 60 * 1000, // 10 minutes - more aggressive than global setting
    cacheTime: 60 * 60 * 1000, // 1 hour - keep in cache longer
    refetchOnMount: false, // Don't refetch when component mounts if data exists
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false, // Don't refetch on reconnect
    enabled: options?.enabled !== false, // Allow explicit disabling
    queryFn: async () => {
      const protocolsResponse = await conversationEvaluationGetEvaluationProtocols();
      const baseProtocols = protocolsResponse.data || [];

      // Categories are already embedded on the protocol response, so we no longer need
      // to fire an extra request for every single category. Removing the secondary
      // `conversationEvaluationGetEvaluationProtocolCategories` call dramatically
      // reduces network‑chatter (one request instead of N + 1).

      const transformedProtocols: TransformedProtocol[] = baseProtocols.map((bp) => {
          if (bp.id === undefined || bp.name === null || bp.name === undefined || bp.languageCode === null || bp.languageCode === undefined) {
            throw new Error(`Protocol with ID ${bp.id} has missing essential data.`);
          }

          // Categories now come directly with the protocol payload. Depending on the
          // generated swagger version the field can be called `categories` (new) or
          // `evaluationProtocolCategories` (legacy) – support both.
          // The live API currently shows 'protocolCategories'.
          const apiCategories =
            (bp as any).protocolCategories ?? // Check the observed name from your screenshot first
            (bp as any).categories ??         // Then the "new" name mentioned in the original comment
            (bp as any).evaluationProtocolCategories ?? // Then the "legacy" name
            [];                               // Default to an empty array if none are found

          const transformedCategories: TransformedCategory[] = apiCategories // Renamed
            .map((cat) => { // Renamed ch to cat
              if (cat.id === undefined || cat.name === null || cat.name === undefined || cat.evaluationProtocolId === undefined || cat.order === undefined) {
                console.warn(`Category with ID ${cat.id} for protocol ${bp.id} has missing essential data, skipping.`); // Renamed
                return null;
              }
              const elements: TransformedElement[] = (cat.protocolElements || []) // Renamed ch to cat
                .map(el => {
                  if (el.id === undefined || el.description === null || el.description === undefined) {
                     console.warn(`Element with ID ${el.id} for category ${cat.id} has missing essential data, skipping.`); // Renamed
                     return null;
                  }
                  // Explicitly construct TransformedElement
                  const transformedElement: TransformedElement = {
                    id: el.id,
                    description: el.description,
                    order: el.order,
                    type: el.type,
                    choices: el.choices,
                    scaleMin: el.scaleMin,
                    scaleMax: el.scaleMax,
                    explanationEnabled: el.explanationEnabled,
                    categoryId: cat.id!, // Renamed
                  };
                  return transformedElement;
                })
                .filter(el => el !== null) as TransformedElement[];

              elements.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

              // Explicitly construct TransformedCategory
              const transformedCategory: TransformedCategory = {
                id: cat.id,
                title: cat.name,
                protocolId: cat.evaluationProtocolId,
                order: cat.order,
                // description: cat.description, // Uncomment and add to interface if needed
                elements,
              };
              return transformedCategory;
            })
            .filter(cat => cat !== null) as TransformedCategory[];

          transformedCategories.sort((a, b) => a.order - b.order);

          // Explicitly construct TransformedProtocol
          const transformedProtocol: TransformedProtocol = {
            id: bp.id,
            title: bp.name, // Map 'name' to 'title'
            languageCode: bp.languageCode,
            // description: bp.description, // Uncomment and add to interface if needed
            categories: transformedCategories,
            otherCompaniesCount: bp.otherCompaniesCount,
            companies: (bp.companies ?? []).map((c) => ({
              companyId: c.companyId ?? 0,
              access: c.access,
            }))
          };
          return transformedProtocol;
        });

      return transformedProtocols;
    },
  });
};

// Protocol CRUD
export const useCreateEvaluationProtocol = (options?: UseMutationOptions<ApiEvaluationProtocolDto, Error, CreateEvaluationProtocolCommand>) => {
  const queryClient = useQueryClient();
  const { onSuccess: onOptionsSuccess, ...restOptions } = options || {};

  return useMutation<ApiEvaluationProtocolDto, Error, CreateEvaluationProtocolCommand, { previousProtocols?: TransformedProtocol[] }>({
    mutationFn: async (command: CreateEvaluationProtocolCommand) => {
      const response = await conversationEvaluationCreateEvaluationProtocol(command);
      return response.data;
    },
    onMutate: async (newProtocolCommand) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'] });

      // Snapshot the previous value
      const previousProtocols = queryClient.getQueryData<TransformedProtocol[]>([EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories']);

      // Optimistically update to the new value
      if (previousProtocols) {
        const optimisticProtocol: TransformedProtocol = {
          id: Date.now(), // Temporary ID
          title: newProtocolCommand.name,
          languageCode: newProtocolCommand.languageCode,
          categories: [], // New protocols start with no categories
        };
        queryClient.setQueryData<TransformedProtocol[]>([EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'], [...previousProtocols, optimisticProtocol]);
      }

      return { previousProtocols };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (err, newProtocolCommand, context) => {
      if (context?.previousProtocols) {
        queryClient.setQueryData<TransformedProtocol[]>([EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'], context.previousProtocols);
      }
    },
    // Always refetch after error or success:
    onSettled: (data, error, variables, context) => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'] });
      if (!error && onOptionsSuccess && data) { // Ensure data is not null for onOptionsSuccess
        onOptionsSuccess(data, variables, context);
      }
    },
    ...restOptions,
  });
};

export const useUpdateEvaluationProtocol = (options?: UseMutationOptions<ApiEvaluationProtocolDto, Error, { protocolId: number; evaluationProtocolDto: UpdateEvaluationProtocolCommand }>) => {
  const queryClient = useQueryClient();
  return useMutation<ApiEvaluationProtocolDto, Error, { protocolId: number; evaluationProtocolDto: UpdateEvaluationProtocolCommand }>({
    mutationFn: async ({ protocolId, evaluationProtocolDto }) => {
      // Construct the command payload, ensuring the ID from the route is included.
      // The evaluationProtocolDto (type UpdateEvaluationProtocolCommand) provides name and languageCode.
      const commandPayload: UpdateEvaluationProtocolCommand = {
        id: protocolId, // Ensure the ID in the payload matches the route ID
        name: evaluationProtocolDto.name,
        languageCode: evaluationProtocolDto.languageCode,
      };

      const response = await conversationEvaluationUpdateEvaluationProtocol(protocolId, commandPayload);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
      // Optionally, update the specific protocol in the cache for immediate UI update.
      // This needs to be done carefully if the response 'data' (ApiEvaluationProtocolDto)
      // has a different structure than 'TransformedProtocol'.
      // For example, if data.name maps to TransformedProtocol.title.
      queryClient.setQueryData([EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'], (oldData: TransformedProtocol[] | undefined) => {
        return oldData?.map(p => {
          if (p.id === variables.protocolId) {
            // Assuming data is ApiEvaluationProtocolDto and variables.evaluationProtocolDto is UpdateEvaluationProtocolCommand
            return {
              ...p,
              title: data.name ?? p.title, // Use API response name, fallback to existing title
              languageCode: data.languageCode ?? p.languageCode, // Use API response languageCode, fallback to existing
            };
          }
          return p;
        });
      });
    },
    ...options,
  });
};

export const useDeleteEvaluationProtocol = (options?: UseMutationOptions<void, Error, number>) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (protocolId: number) => {
      // Assuming the client method is named conversationEvaluationDeleteEvaluationProtocol
      // and takes protocolId as a number.
      // Adjust if the actual client method name or parameters differ.
      await conversationEvaluationDeleteEvaluationProtocol(protocolId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
    },
    ...options,
  });
};

// Category CRUD
export const useCreateEvaluationProtocolCategory = (options?: UseMutationOptions<ApiEvaluationProtocolCategoryDto, Error, { protocolId: number; command: CreateEvaluationProtocolCategoryCommand }>) => {
  const queryClient = useQueryClient();
  return useMutation<ApiEvaluationProtocolCategoryDto, Error, { protocolId: number; command: CreateEvaluationProtocolCategoryCommand }>({
    mutationFn: async ({ protocolId, command }) => {
      const response = await conversationEvaluationCreateEvaluationProtocolCategory(protocolId, command);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
    },
    ...options,
  });
};

export const useUpdateEvaluationProtocolCategory = (options?: UseMutationOptions<ApiEvaluationProtocolCategoryDto, Error, { categoryId: number; command: UpdateEvaluationProtocolCategoryCommand }>) => {
  const queryClient = useQueryClient();
  return useMutation<ApiEvaluationProtocolCategoryDto, Error, { categoryId: number; command: UpdateEvaluationProtocolCategoryCommand }>({
    mutationFn: async ({ categoryId, command }) => {
      const commandPayload: UpdateEvaluationProtocolCategoryCommand = {
        ...command, // Spread other fields from the incoming command
        id: categoryId, // Ensure the ID in the payload matches the route ID
      };
      const response = await conversationEvaluationUpdateEvaluationProtocolCategory(categoryId, commandPayload);
      return response.data;
    },
    onSuccess: (data, variables) => { // data is ApiEvaluationProtocolCategoryDto
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
      queryClient.setQueryData([EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'], (oldData: TransformedProtocol[] | undefined) => {
        return oldData?.map(p => ({
          ...p,
          categories: p.categories.map(cat => {
            if (cat.id === variables.categoryId) {
              return {
                ...cat,
                title: data.name ?? cat.title, // API returns 'name', UI uses 'title'
                order: data.order ?? cat.order,
                // protocolId should not change during an update of the category itself
              };
            }
            return cat;
          }),
        }));
      });
    },
    ...options,
  });
};

export const useDeleteEvaluationProtocolCategory = (options?: UseMutationOptions<void, Error, { categoryId: number }>) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { categoryId: number }>({
    mutationFn: async ({ categoryId }) => {
      await conversationEvaluationDeleteEvaluationProtocolCategory(categoryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
    },
    ...options,
  });
};

export const useReorderEvaluationProtocolCategories = (options?: UseMutationOptions<void, Error, { protocolId: number; orderedCategoryIds: number[] }>) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { protocolId: number; orderedCategoryIds: number[] }>({
    mutationFn: async ({ protocolId, orderedCategoryIds }) => {
      await conversationEvaluationReorderEvaluationProtocolCategories(protocolId, orderedCategoryIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
    },
    ...options,
  });
};


// Element CRUD
export const useCreateEvaluationProtocolElement = (
  options?: UseMutationOptions<ApiEvaluationProtocolElementDto, Error, { categoryId: number; command: CreateEvaluationProtocolElementCommand }>
) => {
  const queryClient = useQueryClient();
  return useMutation<ApiEvaluationProtocolElementDto, Error, { categoryId: number; command: CreateEvaluationProtocolElementCommand }>(
    {
    mutationFn: async ({ categoryId, command }) => {
      const commandWithCategoryId: CreateEvaluationProtocolElementCommand = {
        ...command,
        evaluationProtocolCategoryId: categoryId,
      };
      const response = await conversationEvaluationCreateEvaluationProtocolElement(categoryId, commandWithCategoryId);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
    },
    ...options,
  }
  );
};

export const useUpdateEvaluationProtocolElement = (
  options?: UseMutationOptions<ApiEvaluationProtocolElementDto, Error, { elementId: number; command: UpdateEvaluationProtocolElementCommand }>
) => {
  const queryClient = useQueryClient();
  return useMutation<ApiEvaluationProtocolElementDto, Error, { elementId: number; command: UpdateEvaluationProtocolElementCommand }>(
    {
    mutationFn: async ({ elementId, command }) => {
      const commandPayload: UpdateEvaluationProtocolElementCommand = {
        ...command,
        id: elementId,
        ...(command.evaluationProtocolCategoryId && { evaluationProtocolCategoryId: command.evaluationProtocolCategoryId }),
      };
      const response = await conversationEvaluationUpdateEvaluationProtocolElement(elementId, commandPayload);
      return response.data;
    },
    onSuccess: (data, variables) => { // data is ApiEvaluationProtocolElementDto
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
      queryClient.setQueryData([EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'], (oldData: TransformedProtocol[] | undefined) => {
        return oldData?.map(p => ({
          ...p,
          categories: p.categories.map(cat => ({
            ...cat,
            elements: cat.elements.map(el => {
              if (el.id === variables.elementId) {
                // Map API response to UI element properties
                return {
                  ...el,
                  description: data.description ?? el.description,
                  order: data.order ?? el.order,
                  type: data.type ?? el.type,
                  choices: data.choices ?? el.choices,
                  scaleMin: data.scaleMin ?? el.scaleMin,
                  scaleMax: data.scaleMax ?? el.scaleMax,
                  explanationEnabled: data.explanationEnabled ?? el.explanationEnabled,
                  // categoryId should not change during an update of the element itself
                };
              }
              return el;
            }),
          })),
        }));
      });
    },
    ...options,
  }
  );
};

export const useDeleteEvaluationProtocolElement = (options?: UseMutationOptions<void, Error, { elementId: number }>) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { elementId: number }>({
    mutationFn: async ({ elementId }) => {
      await conversationEvaluationDeleteEvaluationProtocolElement(elementId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
    },
    ...options,
  });
};

export const useReorderEvaluationProtocolElements = (options?: UseMutationOptions<void, Error, { categoryId: number; orderedElementIds: number[] }>) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { categoryId: number; orderedElementIds: number[] }>({
    mutationFn: async ({ categoryId, orderedElementIds }) => {
      await conversationEvaluationReorderEvaluationProtocolElements(categoryId, orderedElementIds);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY] });
    },
    ...options,
  });
};

/**
 * Share an evaluation protocol with multiple companies
 */
export type ProtocolCompanyAccessDto = {
  companyId: number;
  access: AccessLevel;
};

export const useShareEvaluationProtocol = (
  options?: UseMutationOptions<ApiEvaluationProtocolDto, Error, { protocolId: number; companies: ProtocolCompanyAccessDto[] }>
) => {
  const queryClient = useQueryClient();

  return useMutation<ApiEvaluationProtocolDto, Error, { protocolId: number; companies: ProtocolCompanyAccessDto[] }>(
    {
      mutationFn: async ({ protocolId, companies }) => {
        // Construct payload
        const commandPayload: ShareEvaluationProtocolCommand = {
          evaluationProtocolId: protocolId,
          companies,
        };

        const response = await conversationEvaluationShareEvaluationProtocol(protocolId, commandPayload);

        return response.data;
      },
      onSuccess: (data) => {
        // Invalidate or update cache as needed
        queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'] });
      },
      ...options,
    }
  );
};

/**
 * Unshare an evaluation protocol from a company/organization
 */
export const useUnshareEvaluationProtocolOrganization = (
  options?: UseMutationOptions<ApiEvaluationProtocolDto, Error, { protocolId: number; organizationId: number }>
) => {
  const queryClient = useQueryClient();

  return useMutation<ApiEvaluationProtocolDto, Error, { protocolId: number; organizationId: number }>(
    {
      mutationFn: async ({ protocolId, organizationId }) => {
        // Construct payload for UnshareEvaluationProtocolCommand
        const commandPayload: UnshareEvaluationProtocolCommand = {
          evaluationProtocolId: protocolId, // Add protocolId to the command
          companyIds: [organizationId], // API expects an array of company IDs
        };

        // Call the generated API client method
        // The first 'protocolId' is for the path, 'commandPayload' is for the body
        const response = await conversationEvaluationUnshareEvaluationProtocol(protocolId, commandPayload);

        return response.data; // Assuming the API returns the updated protocol DTO or void
      },
      onSuccess: (data, variables, context) => {
        // Invalidate queries to refetch the list of protocols or the specific protocol
        queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'] });
        
        // Optimistically update the cache
        queryClient.setQueryData<TransformedProtocol[] | undefined>(
          [EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'],
          (oldData) => {
            if (!oldData) return undefined;
            return oldData.map(protocol => {
              if (protocol.id === variables.protocolId) {
                return {
                  ...protocol,
                  companies: protocol.companies?.filter(c => c.companyId !== variables.organizationId),
                };
              }
              return protocol;
            }).filter(protocol => protocol.companies === undefined || protocol.companies.length > 0 || (protocol.id !== variables.protocolId)); // Optional: remove protocol if no companies left
          }
        );
        if (options?.onSuccess) {
          options.onSuccess(data, variables, context);
        }
      },
      ...options,
    }
  );
};

/**
 * Update the access level of a company for an evaluation protocol
 */
export const useUpdateEvaluationProtocolCompanyAccess = (
  options?: UseMutationOptions<ApiEvaluationProtocolDto, Error, { protocolId: number; companyId: number; access: AccessLevel }>
) => {
  const queryClient = useQueryClient();

  return useMutation<ApiEvaluationProtocolDto, Error, { protocolId: number; companyId: number; access: AccessLevel }>(
    {
      mutationFn: async ({ protocolId, companyId, access }) => {
        // Construct payload
        const commandPayload: UpdateEvaluationProtocolCompanyAccessCommand = {
          evaluationProtocolId: protocolId,
          companyId: companyId,
          access: access
        };

        const response = await conversationEvaluationUpdateProtocolCompanyAccess(protocolId, companyId, commandPayload);

        return response.data;
      },
      onSuccess: (data) => {
        // Invalidate or update cache as needed
        queryClient.invalidateQueries({ queryKey: [EVALUATION_PROTOCOL_QUERY_KEY, 'allWithCategories'] });
      },
      ...options,
    }
  );
};

// Protocol usage (scenarios using a protocol)
export const useGetProtocolUsage = (protocolId: number) => {
  return useQuery({
    queryKey: [EVALUATION_PROTOCOL_QUERY_KEY, 'usage', protocolId],
    queryFn: ({ signal }) => conversationEvaluationGetProtocolUsage(protocolId, signal).then((res) => res.data),
    enabled: !!protocolId,
    staleTime: 5 * 60 * 1000,
  });
};
